import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getDateObjWithoutTime, addHoursToDateTime } from 'utils/functions/dateTime';
import { fetchAddOrEditCampaignSubmission } from 'modules/campaigns/actions';
import AddCampaignComponent from 'modules/campaigns/components/AddCampaignComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const AddCampaignContainer = () => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { campaignDetails } = useSelector(state => ({
    campaignDetails: state.campaigns.campaignDetails,
  }));

  const dispatch = useDispatch();
  const { id: campaignKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const shouldDisableFormFields = !!campaignKey;
  const advertiserKey = searchParams.get('advertiserKey');
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [formFieldsValues, setFormFieldsValues] = useState({
    name: (shouldDisableFormFields && 'value') || '',
    type: (shouldDisableFormFields && { key: 'value' }) || {},
    billing: (shouldDisableFormFields && { key: 'value' }) || {},
    zone: (shouldDisableFormFields && ['value']) || [],
    startDate:
      (shouldDisableFormFields && getDateObjWithoutTime(new Date())) ||
      getDateObjWithoutTime(new Date()),
    endDate: (campaignKey && campaignDetails?.endDate) || getDateObjWithoutTime(new Date()),
    target: (shouldDisableFormFields && [{ key: 'value', value: 'value' }]) || [
      {
        id: new Date().getTime(),
        type: {},
        key: '',
        value: '',
      },
    ],
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    name: true,
    type: true,
    billing: true,
    zone: true,
    startDate: true,
    endDate: true,
    targetKey: true,
    targetValue: true,
  });
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const addOrEditCampaignSuccessCallback = () => {
    navigate(`/campaigns?advertiserKey=${advertiserKey}`);
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextInputFieldChange = (fieldName, fieldEvent) => {
    const { value } = fieldEvent.target;

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;

      return fieldsObj;
    });
  };

  const handleDropdownFieldChange = (fieldName, fieldValue) => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = fieldValue;

      if (fieldName === 'type')
        fieldsObj.target = [
          {
            id: new Date().getTime(),
            type: {},
            key: '',
            value: '',
          },
        ];

      return fieldsObj;
    });
  };

  const handleMultiSelectionDropdownFieldChange = (fieldName, fieldValue) => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = fieldValue;

      return fieldsObj;
    });
  };

  const handleDatePickerFieldChange = (fieldName, date) => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = date;

      return fieldsObj;
    });
  };

  const handleTargetFieldChange = (fieldName, fieldValue, index) => {
    const targetObj = { ...formFieldsValues.target[index] };
    targetObj[fieldName] = fieldValue;
    if (fieldName === 'type') targetObj.key = fieldValue.mappedName;

    const targetArr = [...formFieldsValues.target];
    targetArr[index] = targetObj;

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj.target = targetArr;

      return fieldsObj;
    });
  };

  const handleAddTargetBtnClick = () => {
    setFormFieldsValues(prevValue => {
      const updatedTarget = [...prevValue.target];
      updatedTarget.push({
        id: new Date().getTime(),
        type: {},
        key: '',
        value: '',
      });

      return {
        ...prevValue,
        target: updatedTarget,
      };
    });
  };

  const handleDeleteTargetBtnClick = index => {
    setFormFieldsValues(prevValue => {
      const updatedTarget = [...prevValue.target];
      updatedTarget.splice(index, 1);

      return {
        ...prevValue,
        target: updatedTarget,
      };
    });
  };

  const handleAddOrEditCampaignBtnClick = () => {
    let isFormValid = true;

    if (
      !formFieldsValues.name ||
      !formFieldsValues.type.key ||
      !formFieldsValues.billing.key ||
      !formFieldsValues.zone.length ||
      !formFieldsValues.startDate ||
      !formFieldsValues.endDate ||
      !formFieldsValues.target[0].key ||
      !formFieldsValues.target[0].value
    ) {
      setFormFieldsValidation({
        name: !!formFieldsValues.name,
        type: !!formFieldsValues.type.key,
        billing: !!formFieldsValues.billing.key,
        zone: !!formFieldsValues.zone.length,
        startDate: !!formFieldsValues.startDate,
        endDate: !!formFieldsValues.endDate,
        targetKey: !!formFieldsValues.target[0].key,
        targetValue: !!formFieldsValues.target[0].value,
      });
      isFormValid = false;
    }

    if (isFormValid === false) return;

    setFormFieldsValidation({
      name: true,
      type: true,
      billing: true,
      target: true,
      startDate: true,
      endDate: true,
    });

    let data = {
      endDate: addHoursToDateTime(formFieldsValues.endDate, 2),
    };
    // this means we are in the add mode
    if (!campaignKey)
      data = {
        ...data,
        advertiserKey,
        campaignName: formFieldsValues.name,
        campaignType: formFieldsValues.type.name,
        pricingModel: formFieldsValues.billing.name,
        price: 0,
        startDate: addHoursToDateTime(formFieldsValues.startDate, 2),
        zoneKeys: formFieldsValues.zone.map(zone => zone.key),
        targetings: formFieldsValues.target.map(target => ({
          targetKeyName: target.key,
          targetKeyValue: target.value,
        })),
      };
    const body = {
      data,
      addOrEditCampaignCallbackActionSuccess: addOrEditCampaignSuccessCallback,
    };
    // this means we are in the edit mode
    if (campaignKey) body.campaignKey = campaignKey;

    dispatch(fetchAddOrEditCampaignSubmission(body));
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <AddCampaignComponent
      shouldDisableFormFields={shouldDisableFormFields}
      formFieldsValues={formFieldsValues}
      formFieldsValidation={formFieldsValidation}
      handleTextInputFieldChange={handleTextInputFieldChange}
      handleDropdownFieldChange={handleDropdownFieldChange}
      handleMultiSelectionDropdownFieldChange={handleMultiSelectionDropdownFieldChange}
      handleDatePickerFieldChange={handleDatePickerFieldChange}
      handleTargetFieldChange={handleTargetFieldChange}
      handleAddTargetBtnClick={handleAddTargetBtnClick}
      handleDeleteTargetBtnClick={handleDeleteTargetBtnClick}
      handleAddOrEditCampaignBtnClick={handleAddOrEditCampaignBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

AddCampaignContainer.propTypes = {};

AddCampaignContainer.defaultProps = {};

export default AddCampaignContainer;
